import { create } from 'zustand';
import * as assessmentService from '../../services/assessmentService';
import * as customValuesService from '../../services/customValuesService';
import * as rubricTemplateService from '../../services/rubricTemplateService';
import { RubricTemplate, RubricTemplateCriterion } from '../types';
import { useAuthStore } from './authStore';

interface Assessment {
  id: string;
  name: string;
  question: string;
  rubric: string;
  systemInstructions?: string;
  criteria?: Array<{
    name: string;
    weight: number;
  }>;
  totalMarks?: number;
  yearLevel?: string;
  subject?: string;
  topic?: string;
  yearGroup?: string;
  year?: string;
  examCondition?: string;
  assessmentType?: string;
  archived?: boolean;
  exemplars?: Array<{
    essayText: string;
    yearLevel?: string;
    criteriaBands: Array<{
      name: string;
      band: string;
    }>;
    overallScore: number;
    explanation?: string;
  }>;
  createdAt?: any;
  userId?: string;
}

interface Essay {
  id: string;
  studentName: string;
  assessmentId: string;
  userId?: string;
  uploadedImages: Array<{
    id: string;
    pageLabel: string;
    previewUrl: string;
  }>;
  transcribedText: string | null;
  rawMarkingRuns: any[];
  consolidatedMarkingResults: any[];
  manualMarkingResults: any[];
  manualOverallScore: number | null;
  teacherFeedback: string;
  varianceExplanation: string | null;
  finalOverallScore: number | null;
  finalOverallBand: string | null;
  finalConfidence: string | null;
  ensembleSpread: {
    min: number;
    max: number;
    stdDev: number;
  } | null;
  feedback: string | null;
  markingPromptUsed: string | null;
  justificationGenerationPromptUsed: string | null;
  metaAnalysisPromptUsed: string | null;
  modelUsed: string | null;
  scoringMethodUsed: string | null;
  scoringCalculationTrace: string | null;
  inputType: string | null;
  createdAt?: any;
}

interface AssessmentState {
  assessments: Assessment[];
  viewingAssessment: Assessment | null;
  selectedAssessmentForEdit: Assessment | null;
  essaysForViewingAssessment: Essay[];
  activeEssayData: Essay | null;
  isLoading: boolean;
  error: string | null;
  showArchived: boolean;
  filters: {
    subject: string | null;
    topic: string | null;
    yearGroup: string | null;
    year: string | null;
    examCondition: string | null;
    assessmentType: string | null;
  };
  customValues: {
    subjects: string[];
    topics: string[];
    yearGroups: string[];
    years: string[];
    examConditions: string[];
    assessmentTypes: string[];
  };
  rubricTemplates: RubricTemplate[];

  // Essay list sorting and filtering
  sortBy: 'studentName' | 'submissionDate' | 'manualScore' | 'aiScore' | 'difference';
  sortDirection: 'asc' | 'desc';
  selectedModelFilters: Set<string>;
  searchQuery: string;
  
  // Actions
  fetchAssessments: () => Promise<void>;
  selectAssessment: (assessment: Assessment) => Promise<void>;
  createAssessment: (assessmentData: Partial<Assessment>) => Promise<void>;
  updateAssessment: (assessmentData: Assessment) => Promise<void>;
  deleteAssessment: (assessmentId: string, assessmentName: string) => Promise<void>;
  archiveAssessment: (assessmentId: string, archive: boolean) => Promise<void>;
  setSelectedAssessmentForEdit: (assessment: Assessment | null) => void;
  toggleShowArchived: () => void;
  setFilter: (filterName: string, value: string | null) => void;
  clearFilters: () => void;
  addCustomValue: (category: string, value: string) => Promise<void>;

  // Rubric template actions
  fetchRubricTemplates: () => Promise<void>;
  saveRubricTemplate: (data: {
    name: string;
    totalMarks: number;
    criteria: RubricTemplateCriterion[];
    sourceAssessmentId?: string;
    overwriteId?: string;
  }) => Promise<void>;
  renameRubricTemplate: (templateId: string, newName: string) => Promise<void>;
  deleteRubricTemplate: (templateId: string) => Promise<void>;

  // Essay actions
  addEssay: (studentName: string, assessmentId: string, inputType: string) => Promise<string>;
  updateEssay: (essayId: string, data: Partial<Essay>) => Promise<void>;
  deleteEssay: (essayId: string, studentName: string) => Promise<void>;
  setActiveEssayData: (essay: Essay | null) => void;
  
  // Essay list UI actions
  setSortBy: (sortBy: 'studentName' | 'submissionDate' | 'manualScore' | 'aiScore' | 'difference') => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  setSelectedModelFilters: (filters: Set<string>) => void;
  setSearchQuery: (query: string) => void;
  
  // UI state
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useAssessmentStore = create<AssessmentState>((set, get) => ({
  assessments: [],
  viewingAssessment: null,
  selectedAssessmentForEdit: null,
  essaysForViewingAssessment: [],
  activeEssayData: null,
  isLoading: false,
  error: null,
  showArchived: false,
  filters: {
    subject: null,
    topic: null,
    yearGroup: null,
    year: null,
    examCondition: null,
    assessmentType: null
  },
  customValues: {
    subjects: [],
    topics: [],
    yearGroups: [],
    years: [],
    examConditions: [],
    assessmentTypes: []
  },
  rubricTemplates: [],

  // Essay list sorting and filtering initial state
  sortBy: 'aiScore',
  sortDirection: 'desc',
  selectedModelFilters: new Set(),
  searchQuery: '',

  fetchAssessments: async () => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    set({ isLoading: true, error: null });
    try {
      const includeArchived = get().showArchived;
      const fetchedAssessments = await assessmentService.getAssessments(userId, includeArchived);
      
      if (!fetchedAssessments) {
        set({ assessments: [] });
        return;
      }
      
      const assessments = fetchedAssessments as Assessment[];
      
      // Load custom values from database
      const storedCustomValues = await customValuesService.getCustomValues(userId);
      
      // Extract values from assessments and merge with stored values
      const mergedCustomValues = customValuesService.extractAndMergeCustomValues(assessments, storedCustomValues);
      
      // Update database with any newly extracted values
      await customValuesService.setCustomValues(userId, mergedCustomValues);
      
      set({
        assessments,
        customValues: mergedCustomValues
      });

      // Keep rubric templates in sync so the create-assessment dropdown is always populated
      await get().fetchRubricTemplates();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch assessments' });
    } finally {
      set({ isLoading: false });
    }
  },

  selectAssessment: async (assessment) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    set({ isLoading: true, error: null, viewingAssessment: assessment });
    try {
      const essays = await assessmentService.getEssaysForAssessment(userId, assessment.id);
      set({ essaysForViewingAssessment: essays as Essay[] });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch essays' });
    } finally {
      set({ isLoading: false });
    }
  },

  createAssessment: async (assessmentData) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    set({ isLoading: true, error: null });
    try {
      await assessmentService.addAssessment(assessmentData, userId);
      await get().fetchAssessments();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create assessment' });
    } finally {
      set({ isLoading: false });
    }
  },

  updateAssessment: async (assessmentData) => {
    set({ isLoading: true, error: null });
    try {
      await assessmentService.updateAssessment(assessmentData);
      await get().fetchAssessments();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update assessment' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteAssessment: async (assessmentId, assessmentName) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    set({ isLoading: true, error: null });
    try {
      await assessmentService.deleteAssessment(assessmentId, userId);
      await get().fetchAssessments();
      if (get().viewingAssessment?.id === assessmentId) {
        set({ viewingAssessment: null, essaysForViewingAssessment: [] });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete assessment' });
    } finally {
      set({ isLoading: false });
    }
  },

  setSelectedAssessmentForEdit: (assessment) => {
    set({ selectedAssessmentForEdit: assessment });
  },

  addEssay: async (studentName, assessmentId, inputType) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) throw new Error('User not authenticated');

    set({ isLoading: true, error: null });
    try {
      const result = await assessmentService.addEssay(userId, assessmentId, studentName, inputType);
      if (!result?.id) throw new Error('Failed to create essay');
      
      // Refresh essays list
      const assessment = get().viewingAssessment;
      if (assessment) {
        await get().selectAssessment(assessment);
      }
      
      return result.id;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add essay' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateEssay: async (essayId, data) => {
    set({ isLoading: true, error: null });
    try {
      await assessmentService.updateEssay(essayId, data);
      
      // Update local state if this is the active essay
      if (get().activeEssayData?.id === essayId) {
        set(state => ({
          activeEssayData: state.activeEssayData ? { ...state.activeEssayData, ...data } : null
        }));
      }
      
      // Refresh essays list
      const assessment = get().viewingAssessment;
      if (assessment) {
        await get().selectAssessment(assessment);
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update essay' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteEssay: async (essayId, studentName) => {
    set({ isLoading: true, error: null });
    try {
      await assessmentService.deleteEssay(essayId);
      
      // Refresh essays list
      const assessment = get().viewingAssessment;
      if (assessment) {
        await get().selectAssessment(assessment);
      }
      
      // Clear active essay if it was deleted
      if (get().activeEssayData?.id === essayId) {
        set({ activeEssayData: null });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete essay' });
    } finally {
      set({ isLoading: false });
    }
  },

  setActiveEssayData: (essay) => {
    set({ activeEssayData: essay });
  },

  setIsLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },
  
  archiveAssessment: async (assessmentId, archive) => {
    set({ isLoading: true, error: null });
    try {
      await assessmentService.archiveAssessment(assessmentId, archive);
      await get().fetchAssessments();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : `Failed to ${archive ? 'archive' : 'unarchive'} assessment` });
    } finally {
      set({ isLoading: false });
    }
  },
  
  toggleShowArchived: () => {
    set(state => ({ showArchived: !state.showArchived }));
    get().fetchAssessments();
  },
  
  setFilter: (filterName, value) => {
    set(state => ({
      filters: {
        ...state.filters,
        [filterName]: value
      }
    }));
  },
  
  clearFilters: () => {
    set({
      filters: {
        subject: null,
        topic: null,
        yearGroup: null,
        year: null,
        examCondition: null,
        assessmentType: null
      }
    });
  },
  
  addCustomValue: async (category, value) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    try {
      // Add to database
      const categoryKey = category as keyof customValuesService.CustomValues;
      await customValuesService.addCustomValue(userId, categoryKey, value);
      
      // Update local state
      set(state => {
        const customValues = { ...state.customValues };
        const stateCategoryKey = `${category}s` as keyof typeof customValues;
        
        if (Array.isArray(customValues[stateCategoryKey]) && !customValues[stateCategoryKey].includes(value)) {
          (customValues[stateCategoryKey] as string[]).push(value);
        }
        
        return { customValues };
      });
    } catch (error) {
      console.error(`Failed to add custom ${category} value:`, error);
      // Still update local state as fallback
      set(state => {
        const customValues = { ...state.customValues };
        const categoryKey = `${category}s` as keyof typeof customValues;
        
        if (Array.isArray(customValues[categoryKey]) && !customValues[categoryKey].includes(value)) {
          (customValues[categoryKey] as string[]).push(value);
        }
        
        return { customValues };
      });
    }
  },
  
  fetchRubricTemplates: async () => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) return;

    try {
      const templates = await rubricTemplateService.getRubricTemplates(userId);
      set({ rubricTemplates: templates });
    } catch (error) {
      console.error('Failed to fetch rubric templates:', error);
    }
  },

  saveRubricTemplate: async ({ name, totalMarks, criteria, sourceAssessmentId, overwriteId }) => {
    const userId = useAuthStore.getState().user?.uid;
    if (!userId) throw new Error('User not authenticated');

    const payload = {
      name: name.trim(),
      totalMarks,
      criteria,
      ...(sourceAssessmentId ? { sourceAssessmentId } : {})
    };

    if (overwriteId) {
      await rubricTemplateService.updateRubricTemplate(overwriteId, payload);
    } else {
      await rubricTemplateService.addRubricTemplate(userId, payload);
    }
    await get().fetchRubricTemplates();
  },

  renameRubricTemplate: async (templateId, newName) => {
    await rubricTemplateService.updateRubricTemplate(templateId, { name: newName.trim() });
    await get().fetchRubricTemplates();
  },

  deleteRubricTemplate: async (templateId) => {
    await rubricTemplateService.deleteRubricTemplate(templateId);
    await get().fetchRubricTemplates();
  },

  // Essay list UI setter implementations
  setSortBy: (sortBy) => {
    set({ sortBy });
  },
  
  setSortDirection: (sortDirection) => {
    set({ sortDirection });
  },
  
  setSelectedModelFilters: (selectedModelFilters) => {
    set({ selectedModelFilters });
  },
  
  setSearchQuery: (searchQuery) => {
    set({ searchQuery });
  },
}));

// Subscribe to auth changes to reset state when user logs out
useAuthStore.subscribe((state) => {
  if (!state.user) {
    useAssessmentStore.setState({
      assessments: [],
      viewingAssessment: null,
      selectedAssessmentForEdit: null,
      essaysForViewingAssessment: [],
      activeEssayData: null,
      rubricTemplates: [],
      error: null
    });
  }
});
